import cookie from 'cookie';
import hasPeopleSubscriptionInInventory from '@/assets/js/helpers/hasPeopleSubscriptionInInventory';
import getPeopleSubscriptionRoute from '@/assets/js/helpers/getPeopleSubscriptionRoute';
import isAccessDeniedError from '@/assets/js/helpers/isAccessDeniedError';

/**
 * One lookup per store, so concurrent callers share a single inventory
 * request. Keyed by store because the server builds one per request, so
 * different users never share a lookup.
 */
const inFlightPeopleHistory = new WeakMap();

/**
 * Creates the billing SDK for the account's billing version.
 * @param {object} [opts={}]
 * @param {boolean} [opts.isV2=false] Account is on billing v2.
 * @param {object} [opts.req] SSR request; absent in the browser.
 * @param {number|string} [opts.serverPort] Local server port, for SSR.
 * @returns {Promise<object>} billing SDK instance
 * @remarks In the browser it matches the dashboard layout's SDK. On the server
 * it calls localhost (bypassing Cloudflare/Radware, like the travelling SDK)
 * with the session cookies the browser would have sent.
 */
const createAccountBillingSDK = async (opts = {}) => {
  // Loaded on demand: the store ships with every page, and the SDKs are large
  const { default: sdk } = opts.isV2
    ? await import('@trazi/billingv2/sdk/node')
    : await import('@trazi/billing/sdk/node');

  if (process.client) {
    return sdk(`${window.location.origin}/api`);
  }

  const cookies = cookie.parse(opts.req?.headers?.cookie ?? '');
  const cookieHeader = ['trav:tok', 'trav:ssid', 'trav:ls']
    .filter(name => cookies[name])
    .map(name => `${name}=${cookies[name]}`)
    .join('; ');

  return sdk(`http://localhost:${opts.serverPort}/api`, {
    headers: { Cookie: cookieHeader }
  });
};

export const state = () => ({
  inventoryItemsObj: {
    subscriptions: [],
    products: []
  },
  // null until resolved; see setPeopleSubscriptionHistory
  hasPeopleSubscriptionHistory: null
});

export const mutations = {
  setInventoryItemsObj(state, inventoryItemsObj) {
    state.inventoryItemsObj = inventoryItemsObj;
  },
  setHasPeopleSubscriptionHistory(state, hasHistory) {
    state.hasPeopleSubscriptionHistory = hasHistory;
  }
};

export const actions = {
  /**
   * The signed-in account's raw inventory, disabled subscriptions included:
   * inventoryItemsObj keeps only enabled ones, which hides lapsed plans.
   * @returns {Promise<Array|null>} null without an account id or on failure
   */
  async getInventory({ rootState }) {
    const currentUser = rootState.auth?.currentUser ?? {};

    if (!currentUser.id) {
      return null;
    }

    const isV2 = !!currentUser.user_data?.v2;
    const domain =
      this.$config.environment === 'production'
        ? `${this.$config.website}.com`
        : this.$config.testingAccountDomain;

    try {
      const sdk = await createAccountBillingSDK({
        isV2,
        req: this.app?.context?.req,
        serverPort: this.$config.serverPort
      });
      const inventory = isV2
        ? await sdk.Inventories.byAccount(domain, currentUser.id, true)
        : await sdk.Billing.Inventories.byAccount(domain, currentUser.id, true);

      // Error payloads and "Access Denied" come back resolved, not thrown
      if (!Array.isArray(inventory)) {
        throw new Error(
          inventory?.msg ?? `Invalid inventory response: ${inventory}`
        );
      }

      return inventory;
    } catch (err) {
      if (!(err instanceof Error)) {
        err = new Error(err?.err?.message ?? err);
      }

      // An expired session is handled by the auth store, so it isn't reported
      if (isAccessDeniedError({ error: err })) {
        return null;
      }

      console.error(err);
      this.$bugsnag?.notify(err, event => {
        event.addMetadata('Extra Info', {
          page: 'store/billing.js',
          component: 'billing',
          method: 'getInventory',
          sdk: isV2 ? 'billingV2SDK' : 'billingSDK',
          sdkMethod: isV2
            ? 'Inventories.byAccount'
            : 'Billing.Inventories.byAccount',
          accountId: currentUser.id
        });
      });
      return null;
    }
  },
  /**
   * Whether the account ever held a People Reports subscription — permissions
   * alone cannot tell a lapsed subscriber from one who never had it. Cached.
   * Resolves null when unresolved, so callers must not test truthiness.
   * @returns {Promise<boolean|null>}
   */
  async setPeopleSubscriptionHistory({ commit, dispatch, state, rootState }) {
    if (state.hasPeopleSubscriptionHistory !== null) {
      return state.hasPeopleSubscriptionHistory;
    }

    // Share one request: the guard above only trips once a result is committed
    if (inFlightPeopleHistory.has(this)) {
      return inFlightPeopleHistory.get(this);
    }

    const request = (async () => {
      let peopleSubscriptionIds = [];

      try {
        const people =
          this.$config.environment === 'production'
            ? require(`@/assets/cms/subscribe/production/${this.$config.website}.com/people.json`)
            : require(`@/assets/cms/subscribe/staging/${this.$config.testingAccountDomain}/people.json`);
        // Legacy v1 plans count as history too
        peopleSubscriptionIds = [
          ...(people.related_subscriptions ?? []),
          ...(people.related_subscriptions_v2 ?? [])
        ].map(({ id }) => id);
      } catch (err) {
        this.$bugsnag?.notify(err, event => {
          event.addMetadata('Extra Info', {
            page: 'store/billing.js',
            component: 'billing',
            method: 'setPeopleSubscriptionHistory',
            accountId: rootState.auth?.currentUser?.id
          });
        });
        return null;
      }

      const inventory = await dispatch('getInventory');

      // getInventory returns null on failure; caching false would stick
      if (!Array.isArray(inventory)) {
        return null;
      }

      const hasHistory = hasPeopleSubscriptionInInventory({
        inventory,
        peopleSubscriptionIds
      });

      commit('setHasPeopleSubscriptionHistory', hasHistory);
      return hasHistory;
    })().finally(() => {
      inFlightPeopleHistory.delete(this);
    });

    inFlightPeopleHistory.set(this, request);
    return request;
  }
};

export const getters = {
  inventoryItemsObj: state => state.inventoryItemsObj,
  hasPeopleSubscriptionHistory: state => state.hasPeopleSubscriptionHistory,
  // Single decision point for every people-subscription CTA in the dashboard
  peopleSubscriptionRoute: state =>
    getPeopleSubscriptionRoute({
      hasHistory: state.hasPeopleSubscriptionHistory
    })
};

export default {
  state,
  mutations,
  actions,
  getters
};
